import {
  Component,
  inject,
  effect,
  ChangeDetectorRef,
  computed,
  signal,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import {
  KENDO_BUTTONGROUP,
  KENDO_BUTTONS,
} from '@progress/kendo-angular-buttons'

import {
  GeneratedContent,
  DeploymentRequirements,
  Enums,
} from '@cyborg-monorepo/hunter/common'
import {
  TabBarComponent,
  TabComponent,
  TypographyComponent,
  ButtonComponent,
  BadgeComponent,
  ButtonGroupComponent,
} from '@hyperkit'

const EMPTY_REQUIREMENTS: DeploymentRequirements = {
  categories: [],
  searchable_data: [],
  deployment_steps: '',
  input_data: [],
}

const EMPTY_QUERY: GeneratedContent = {
  goal: '',
  query: '',
  matched: false,
  category: '',
  missingFields: [],
  deployment_requirements: EMPTY_REQUIREMENTS,
}

import { IconValuesType } from 'pkgs/hyperkit/src/components/icon/icon.component.types'

import { QueryEditorService } from '@hunt/services'

import { QueryStatusColorPipe } from '../../../../../modules/shared/pipes/query-status-color.pipe'
import { NavigationService } from '../../../services/navigation.service'
import { PackageViewService } from '../../../services/reader/package-view.service'
import { QueryViewService } from '../../../services/reader/query-view.service'
import { SessionService } from '../../../services/session.service'

import {
  OverviewTabComponent,
  EmulationValidationTabComponent,
  DeploymentRequirementsTabComponent,
} from './components'

@Component({
  selector: 'package-details-query-selected-view',
  standalone: true,
  imports: [
    TabBarComponent,
    TabComponent,
    TypographyComponent,
    ButtonComponent,
    BadgeComponent,
    OverviewTabComponent,
    EmulationValidationTabComponent,
    DeploymentRequirementsTabComponent,
    FormsModule,
    ButtonGroupComponent,
    KENDO_BUTTONS,
    KENDO_BUTTONGROUP,
    QueryStatusColorPipe,
  ],
  templateUrl: './query-selected-view.component.html',
  styleUrl: './query-selected-view.component.scss',
})
export class QuerySelectedViewComponent {
  private readonly cdr = inject(ChangeDetectorRef)
  public readonly nav = inject(NavigationService)
  private readonly session = inject(SessionService)
  public readonly queryEditor = inject(QueryEditorService)

  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )

  /** read‐only view of the current query/pivot node */
  private readonly selectedQuery = inject(QueryViewService).selectedQuery
  readonly query = signal<GeneratedContent>(EMPTY_QUERY)
  readonly toolType = inject(QueryViewService).selectedToolType
  readonly huntCategory = inject(PackageViewService).huntCategory

  /** which pivotTab are we on (overview|deployment|emulation) */
  readonly selectedTab = this.nav.pivotTab
  readonly statuses = Object.values(Enums.QueryStatusEnum)

  readonly statusButtons = computed(() =>
    this.statuses.map((status) => ({
      label: status,
      selected: this.query().status === status,
      icon: 'circle-filled' as IconValuesType,
      onClick: () => this.updateQueryStatus(status),
    })),
  )

  constructor() {
    // trigger ChangeDetector on signal updates
    effect(() => {
      const q = this.selectedQuery()
      if (q) {
        this.query.set(q)
      }
      this.selectedTab()
      this.cdr.detectChanges()
    })
  }

  updateQueryStatus(statusOption?: string): void {
    this.queryEditor.updateQueryContent({
      status: statusOption as Enums.QueryStatusEnum,
    })
  }

  isSelectedTab(name: 'overview' | 'deployment' | 'emulation'): boolean {
    return this.selectedTab() === name
  }
}
